import { CANCELLATION_REASONS, type CancellationReasonCode } from '@utazzo/shared';
import { UzAlert, UzButton, UzDialog, UzSelect, UzSkeleton, UzTextarea } from '@utazzo/ui';
import { Ban } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { money } from '../booking-format';
import { readRefusal } from '../travel-bookings.api';
import { useCancelQuote, useCancelTravelBooking } from '../use-travel-bookings';

import { KeyValueList } from './KeyValueList';

/**
 * "Cancel booking" on a confirmed booking, on the guest's behalf.
 *
 * Shows the supplier's charge before anything is sent: what the guest paid,
 * what the supplier will keep, what it credits back to us. The confirm sends
 * the charge figure along, and the server refuses if it moved in between -
 * the same guard the guest's own cancel button has.
 *
 * What this does NOT do is refund the guest. The supplier settles the
 * cancellation asynchronously; when it has, admins are notified and the
 * refund is a separate, deliberate decision with its own dialog.
 */
export const CancelBookingDialog = ({
  open,
  onOpenChange,
  bookingId,
  reference,
  product,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId: string;
  reference: string;
  product: 'FLIGHT' | 'HOTEL';
  onDone: () => void;
}): React.JSX.Element => {
  const quote = useCancelQuote(open ? bookingId : null);
  const cancel = useCancelTravelBooking();
  const [reason, setReason] = useState<CancellationReasonCode>('OTHER');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setReason('OTHER');
      setNote('');
    }
  }, [open]);

  const q = quote.data;
  const c = q?.currencyCode ?? 'INR';
  const refusal = quote.isError ? readRefusal(quote.error) : null;
  const supplier = product === 'HOTEL' ? 'the hotel' : 'the airline';
  const blocked = !q || !q.isChargeKnown || q.requestPending;
  const expected = q ? BigInt(q.guestPaidPaise) - BigInt(q.chargePaise) : null;

  const confirm = (): void => {
    if (!q || blocked) return;
    cancel.mutate(
      {
        id: bookingId,
        input: {
          reasonCode: reason,
          acknowledgedChargePaise: q.chargePaise,
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          onDone();
        },
        onError: (e) => {
          const r = readRefusal(e);
          toast.error(r?.message ?? 'The cancellation could not be sent. Try again in a moment.', {
            duration: 10_000,
          });
          if (r?.code === 'CHARGE_CHANGED') void quote.refetch();
          if (r?.code === 'ALREADY_REQUESTED' || r?.code === 'ALREADY_CANCELLED') {
            onOpenChange(false);
            onDone();
          }
        },
      },
    );
  };

  return (
    <UzDialog
      open={open}
      onOpenChange={(next) => {
        if (cancel.isPending) return;
        onOpenChange(next);
      }}
      title={`Cancel booking - ${reference}`}
      description={`Raises the cancellation with ${supplier}. The booking stays live until the supplier confirms; the guest's refund is decided separately once it has.`}
      size="md"
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <UzButton
            variant="ghost"
            className="w-full sm:w-auto"
            onClick={() => onOpenChange(false)}
            disabled={cancel.isPending}
          >
            Keep the booking
          </UzButton>
          <UzButton
            variant="destructive"
            className="w-full sm:w-auto"
            onClick={confirm}
            disabled={blocked || cancel.isPending}
            loading={cancel.isPending}
            leftIcon={<Ban className="h-4 w-4" />}
          >
            {q && q.isChargeKnown
              ? `Cancel with a ${money(q.chargePaise, c)} charge`
              : 'Cancel booking'}
          </UzButton>
        </div>
      }
    >
      {quote.isLoading ? (
        <div className="flex flex-col gap-3">
          <UzSkeleton className="h-20 w-full" />
          <UzSkeleton className="h-10 w-full" />
        </div>
      ) : quote.isError || !q ? (
        <UzAlert
          tone="destructive"
          title="Cannot quote this cancellation"
          description={refusal?.message ?? 'The supplier did not answer. Close and try again.'}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {q.requestPending && (
            <UzAlert
              tone="warning"
              title="A cancellation is already with the supplier"
              description="Wait for the supplier to settle it. You will be notified, and can refund the guest then."
            />
          )}
          {!q.isChargeKnown && (
            <UzAlert
              tone="destructive"
              title="The supplier would not quote a charge"
              description="An unknown charge is not a free cancellation. Confirm it with the supplier by phone before cancelling."
            />
          )}
          <div className="flex flex-col gap-2">
            <KeyValueList
              items={[
                { term: 'Guest paid', value: money(q.guestPaidPaise, c), strong: true },
                {
                  term: 'Supplier charge',
                  value: q.isChargeKnown ? money(q.chargePaise, c) : 'unknown',
                },
                {
                  term: 'Supplier credits us',
                  value: q.isChargeKnown ? money(q.refundPaise, c) : 'unknown',
                },
                {
                  term: 'Guest paid minus charge',
                  value: expected !== null && q.isChargeKnown ? money(expected, c) : '-',
                },
                ...(q.band ? [{ term: 'Policy band', value: `from ${q.band.fromDate}` }] : []),
              ]}
            />
            {q.note && <p className="text-xs text-zinc-600">{q.note}</p>}
            <p className="text-[11px] text-zinc-500">
              The credit is what the supplier returns to our account. What goes back to the guest is
              your decision, made from the refund dialog after the supplier settles.
            </p>
          </div>
          <UzSelect<CancellationReasonCode>
            label="Reason"
            value={reason}
            onValueChange={setReason}
            options={CANCELLATION_REASONS.map((r) => ({ value: r.code, label: r.label }))}
          />
          <UzTextarea
            label="Note to the supplier (optional)"
            placeholder="Appended to the supplier's remarks - keep it factual."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={300}
          />
        </div>
      )}
    </UzDialog>
  );
};
